import React from 'react';
import PropTypes from 'prop-types';
import isNil from 'utils/isNil';
import styled from 'styled-components';
import HyperLink from 'components/Core/HyperLink';
import WebsiteImage from 'components/WebsiteImage';
import { STIcon } from 'components/Brand/Icons/STIcon';

const FeatureBrowseAll = ({ background, title, features, ...otherProps }) => {
  let newFeatures = [];
  let categories = [];

  const categoryTitleExists = features.find((feature) => feature.categoryTitle);

  if (categoryTitleExists) {
    features.forEach((feature) => {
      let categoryIndex = categories.indexOf(feature.categoryTitle);

      if (categoryIndex === -1) {
        categories.push(feature.categoryTitle);
        newFeatures.push({ categoryTitle: feature.categoryTitle, items: [] });
      } else {
        newFeatures[categoryIndex].items.push(feature);
      }
    });
  } else {
    newFeatures = [{ items: [] }];
    features.forEach((feature) => {
      newFeatures[0].items.push(feature);
    });
  }

  return (
    <SectionBrowseAll background={background} {...otherProps} id={otherProps.anchorId}>
      <div className="section-browse-all--container">
        <div className="section-browse-all--title-container">
          <h2 className="section-browse-all--title">{title}</h2>
        </div>
        <div className="section-browse-all--content-container">
          {newFeatures.map((category, index) => {
            return (
              <div className="section-browse-all--category" key={index}>
                <h2 className="section-browse-all--category-title">{category.categoryTitle}</h2>
                <div className="section-browse-all--category-content">
                  {category.items.map((item) => {
                    return (
                      <li className="section-browse-all--category-item" key={item.id}>
                        <HyperLink data-position="Body - InPageNavigation CTA" href={item.link}>
                          {item.icon ? (
                            <WebsiteImage
                              className="section-browse-all--category-item--icon"
                              {...item.icon}
                            />
                          ) : (
                            <STIcon
                              className="section-browse-all--category-item--icon"
                              type={item.iconType}
                            />
                          )}

                          <div className="section-browse-all--category-item--content">
                            <p className="section-browse-all--category-item--title">{item.title}</p>
                            {!isNil(item.description) && (
                              <p className="section-browse-all--category-item--description">
                                {item.description.description}
                              </p>
                            )}
                          </div>
                        </HyperLink>
                      </li>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SectionBrowseAll>
  );
};

FeatureBrowseAll.propTypes = {
  title: PropTypes.string,
  features: PropTypes.array,
  background: PropTypes.string,
};

export default FeatureBrowseAll;

const SectionBrowseAll = styled.div`
  background: ${(props) => props.background};

  .section-browse-all--container {
    max-width: 1236px;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    padding: 80px 28px;

    .section-browse-all--title-container {
      .section-browse-all--title {
        color: var(--darkest);
      }
    }

    .section-browse-all--content-container {
      margin-top: 32px;
      width: 100%;
      padding: 0;

      .section-browse-all--category {
        padding: 48px 0 36px 0;
        border-bottom: 1px solid var(--grey-3);

        &:first-child {
          padding-top: 0;
        }

        &:last-child {
          padding-bottom: 0;
          border-bottom: none;
        }

        .section-browse-all--category-title {
          font-size: var(--font-size-16);
          letter-spacing: 0.04em;
          color: var(--darkest);
          opacity: 0.6;
          text-transform: uppercase;
        }

        .section-browse-all--category-content {
          margin-top: 12px;

          .section-browse-all--category-item {
            vertical-align: middle;
            list-style: none;
            width: 33%;
            display: inline-block;
            padding: 12px 0;

            & > a {
              display: flex;
              align-items: start;

              .section-browse-all--category-item--icon,
              .styles-container {
                width: 40px;
                height: 40px;
              }

              .section-browse-all--category-item--content {
                padding: 0 12px;
                width: 80%;

                .section-browse-all--category-item--title {
                  line-height: 38px;
                  color: var(--darkest);
                  margin-bottom: 0;
                }

                .section-browse-all--category-item--description {
                  font-size: var(--font-size-13);
                  line-height: 21px;
                  color: var(--grey-8);
                  margin: 0;
                  padding: 0;
                }
              }

              &:hover {
                .section-browse-all--category-item--content {
                  .section-browse-all--category-item--title {
                    color: var(--grey-8);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  @media (max-width: 767px) {
    .section-browse-all--container {
      padding: 48px 28px;

      .section-browse-all--title-container {
        .section-browse-all--title {
          font-size: var(--font-size-mobile-h2);
          letter-spacing: var(--letter-spacing--0-03);
        }
      }
      .section-browse-all--content-container {
        margin-top: 24px;

        .section-browse-all--category {
          padding: 32px 0 24px 0;

          .section-browse-all--category-title {
            font-size: var(--font-size-16);
          }
          .section-browse-all--category-content {
            margin-top: 8px;

            .section-browse-all--category-item {
              width: 50%;
              padding: 8px 0;

              & > a {
                .section-browse-all--category-item--content {
                  padding: 0;
                  width: 100%;

                  .section-browse-all--category-item--title {
                    font-size: var(--font-size-18);
                    line-height: 38px;
                    padding: 0 0 0 12px;
                    margin: 0;
                  }

                  .section-browse-all--category-item--description {
                    display: none;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  @media (max-width: 575px) {
    .section-browse-all--container {
      padding: 48px 20px;

      .section-browse-all--content-container {
        .section-browse-all--category {
          .section-browse-all--category-content {
            .section-browse-all--category-item {
              width: 100%;
            }
          }
        }
      }
    }
  }
`;
